import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts'
import {
  buildWinLossMix,
  getPipelineWinRateDisplay,
  type PipelineTabView,
} from '@/components/Dashboard/pipelineTab.utils'
import { formatDashboardNumber } from '@/utils/crmValuesDisplay'

type Props = {
  view: PipelineTabView
  valuesVisible?: boolean
}

export function PipelineWinLossPanel({ view, valuesVisible = false }: Props) {
  const chartData = buildWinLossMix(view)
  const winRate = getPipelineWinRateDisplay(view, valuesVisible)
  const totalClosed = view.closedCount

  return (
    <div className='flex h-full flex-col rounded-2xl border border-[var(--border)] bg-[var(--bg-card)] p-5 shadow-sm'>
      <div>
        <h3 className='text-base font-semibold text-text-primary'>Ganhos vs perdas</h3>
        <p className='mt-1 text-sm text-text-muted'>Mix de fechamentos no período</p>
      </div>

      {totalClosed > 0 && chartData.length > 0 ? (
        <>
          <div className='relative mt-4 h-[180px]'>
            <ResponsiveContainer width='100%' height='100%'>
              <PieChart>
                <Pie
                  data={chartData}
                  dataKey='value'
                  nameKey='name'
                  cx='50%'
                  cy='50%'
                  innerRadius={52}
                  outerRadius={76}
                  paddingAngle={2}
                  stroke='none'
                >
                  {chartData.map((entry) => (
                    <Cell key={entry.name} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'var(--bg-card)',
                    border: '1px solid var(--border)',
                    borderRadius: '10px',
                  }}
                  formatter={(value, name) => [
                    formatDashboardNumber(Number(value ?? 0), valuesVisible),
                    String(name ?? ''),
                  ]}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className='pointer-events-none absolute inset-0 flex flex-col items-center justify-center'>
              <span className='text-2xl font-bold tabular-nums text-text-primary'>
                {winRate.value}
              </span>
              <span className='text-[10px] font-medium uppercase tracking-wide text-text-muted'>
                win rate
              </span>
            </div>
          </div>

          <ul className='mt-3 space-y-2'>
            {chartData.map((row) => (
              <li key={row.name} className='flex items-center justify-between text-sm'>
                <span className='flex items-center gap-2 text-text-secondary'>
                  <span
                    className='h-2.5 w-2.5 rounded-full'
                    style={{ backgroundColor: row.color }}
                    aria-hidden
                  />
                  {row.name}
                </span>
                <span className='font-semibold tabular-nums text-text-primary'>
                  {formatDashboardNumber(row.value, valuesVisible)}
                </span>
              </li>
            ))}
          </ul>
        </>
      ) : (
        <div className='flex flex-1 flex-col items-center justify-center gap-2 py-10 text-center'>
          <p className='text-2xl font-bold text-text-primary'>{winRate.value}</p>
          <p className='text-sm text-text-muted'>{winRate.hint ?? 'Sem fechamentos no período'}</p>
        </div>
      )}
    </div>
  )
}
